import React, {useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import HomeIcon from "@mui/icons-material/Home";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import InventoryIcon from "@mui/icons-material/Inventory";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BarChartIcon from "@mui/icons-material/BarChart";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {Shell} from "../../common/shell/shell.page";
import {PageHeader} from "../../common/components/page-header";

type Article = {
  title: string;
  paragraphs: string[];
  steps?: string[];
  tips?: string[];
  warning?: string;
};

type Module = {
  id: string;
  label: string;
  color: string;
  icon: React.ReactElement;
  description: string;
  articles: Article[];
};

const modules: Module[] = [
  {
    id: "principal",
    label: "Principal",
    color: "#2dd4bf",
    icon: <HomeIcon fontSize="small" />,
    description: "Navegação, painel de início e configuração inicial do sistema.",
    articles: [
      {
        title: "Entendendo a estrutura de navegação",
        paragraphs: [
          "O Symplou é organizado em um menu lateral fixo, dividido em grupos temáticos que refletem as grandes áreas do negócio: vendas, catálogo, financeiro e administração. Cada grupo agrupa funcionalidades relacionadas para que você localize o que precisa sem ter que memorizar caminhos complexos.",
          "No topo do menu aparece o nome do seu negócio. Abaixo dele, o grupo 'Principal' dá acesso ao painel de início e à tela de venda rápida. Os demais grupos aparecem ou somem conforme as permissões do usuário logado — um operador de caixa, por exemplo, não verá opções de configurações ou relatórios que ele não tem autorização para acessar.",
          "Em dispositivos com tela menor (smartphones e tablets em modo retrato), o menu se recolhe automaticamente. Toque no ícone de três barras no canto superior esquerdo para abri-lo. O comportamento é o mesmo — os grupos e itens disponíveis são idênticos aos da versão desktop.",
        ],
        tips: [
          "O item de menu que corresponde à página atual fica destacado com um fundo teal. Isso facilita identificar onde você está sem precisar ler a URL.",
          "Se você precisa navegar rapidamente entre duas telas frequentes, pode manter uma aba do browser para cada uma delas.",
        ],
      },
      {
        title: "Painel de início: o que você encontra ali",
        paragraphs: [
          "A tela de início é o ponto de partida após o login. Ela exibe uma saudação contextual e um resumo das principais funcionalidades disponíveis para o seu perfil de usuário.",
          "Se o módulo de Estatísticas e Relatórios estiver ativo na assinatura e o usuário tiver a permissão de Analytics, um card aparece oferecendo acesso direto ao painel de gráficos. Caso o módulo esteja inativo, o mesmo card exibe um atalho para ativá-lo em Configurações, evitando que você precise navegar manualmente até lá.",
          "O painel de início não exibe dados em tempo real como faturamento do dia ou alertas de estoque — seu propósito é orientação e atalhos. Para dados operacionais em tempo real, use os módulos específicos de Vendas, Estoque e Caixa.",
        ],
        tips: [
          "Administradores costumam acessar o painel de início apenas para verificar se há notícias ou para redirecionar novos colaboradores ao treinamento inicial.",
        ],
      },
      {
        title: "Configuração inicial recomendada para novos negócios",
        paragraphs: [
          "Ao acessar o Symplou pela primeira vez, há uma sequência lógica de configurações que economiza retrabalho e garante que tudo funcione de forma integrada desde o começo.",
          "Comece pelo nome do negócio, que aparece no topo do menu e em relatórios. Em seguida, cadastre as categorias de produtos antes de começar a criar o catálogo — é muito mais trabalhoso categorizar produtos retroativamente. Depois cadastre os fornecedores para já vinculá-los aos produtos no momento do cadastro.",
          "Invite os colaboradores e defina as permissões corretas antes de qualquer operação. Isso evita que vendas sejam registradas sem o usuário correto identificado. Por último, configure o percentual de comissão padrão em Configurações caso pretenda usar o módulo de Comissões.",
        ],
        steps: [
          "Acesse Configurações > Negócio e defina o nome do estabelecimento.",
          "Vá em Produtos > Categorias e crie as categorias do seu catálogo.",
          "Vá em Produtos > Fornecedores e cadastre os fornecedores.",
          "Acesse Usuários e convide os colaboradores com suas permissões.",
          "Em Configurações, defina o percentual de comissão padrão.",
          "Comece a cadastrar produtos em Produtos > Novo produto.",
          "Realize uma venda de teste em Vender para validar o fluxo completo.",
        ],
        tips: [
          "Se você tem um catálogo grande, use o Cadastro em Massa (Produtos > Cadastro em massa) para importar todos os itens via planilha antes de abrir para operação.",
        ],
      },
    ],
  },
  {
    id: "vender",
    label: "Vender",
    color: "#818cf8",
    icon: <StorefrontIcon fontSize="small" />,
    description: "Ponto de venda, adição de produtos, pagamentos e copiloto de IA.",
    articles: [
      {
        title: "Iniciando uma nova venda",
        paragraphs: [
          "Clique em 'Vender' no menu lateral para abrir o ponto de venda. O sistema cria automaticamente uma nova sessão de venda com um identificador único e a registra como 'em andamento'. Esse mecanismo garante que, mesmo que a página seja fechada acidentalmente, os itens adicionados até aquele momento sejam preservados e a venda possa ser retomada.",
          "A interface do ponto de venda é dividida em duas áreas principais: à esquerda, o painel de busca e adição de produtos; à direita (ou abaixo, em mobile), o carrinho com os itens já adicionados, o subtotal e as opções de finalização.",
          "O campo de busca de produtos fica em foco automaticamente ao abrir a tela, permitindo que você comece a digitar imediatamente sem precisar clicar primeiro — especialmente útil em ambientes com teclado físico conectado.",
        ],
        tips: [
          "Em estabelecimentos com alto volume de atendimento, deixe o leitor de código de barras USB conectado. Ele funciona como um teclado e insere o código automaticamente no campo de busca, acelerando muito o processo.",
        ],
      },
      {
        title: "Adicionando produtos por nome, código de barras ou QR Code",
        paragraphs: [
          "Existem três formas de adicionar um produto à venda. A primeira é a busca por nome: comece a digitar no campo de busca e o sistema exibe sugestões em tempo real, filtrando por correspondência parcial no nome do produto. Selecione o item desejado na lista.",
          "A segunda é o código de barras via leitor USB: com o leitor conectado, aponte para o código. O número é inserido automaticamente no campo de busca e o produto correspondente é adicionado sem necessidade de confirmação manual.",
          "A terceira é o scanner de câmera integrado: clique no ícone de câmera na barra de busca para ativar o scanner. O sistema usa a câmera do dispositivo para ler QR Codes ou códigos de barras. Após a leitura, o produto é identificado e adicionado. Essa opção é especialmente útil em tablets e smartphones usados como PDV móvel.",
          "Após adicionar um produto, você pode ajustar a quantidade diretamente no carrinho clicando nos botões '+' e '−', ou digitando o valor no campo de quantidade. Para remover um item, clique no ícone de lixeira ao lado do produto.",
        ],
        tips: [
          "Se o produto não for encontrado pela busca, verifique se ele está cadastrado no catálogo e se o nome digitado corresponde ao nome oficial do produto no sistema.",
          "Produtos sem estoque cadastrado podem ser adicionados normalmente. O controle de estoque só debita automaticamente quando o produto tem saldo inicial configurado.",
        ],
        warning:
          "Se um produto não tiver código de barras cadastrado, a leitura via scanner não o localizará. Certifique-se de preencher o campo de código de barras no cadastro do produto.",
      },
      {
        title: "Formas de pagamento: como funcionam e quando usar cada uma",
        paragraphs: [
          "O Symplou suporta cinco métodos de pagamento: Dinheiro, Cartão de Crédito, Cartão de Débito, Pix e Fiado. Cada um registra a venda de forma diferente no relatório financeiro e no controle de caixa.",
          "Dinheiro: ao selecionar, o sistema calcula automaticamente o troco caso o valor recebido seja maior que o total. Se o controle de caixa estiver ativo, o valor é creditado na sessão atual como entrada em dinheiro.",
          "Cartão de Crédito e Débito: são registrados como pagamento eletrônico. O Symplou não processa a maquininha — ele apenas registra que o pagamento foi feito por esse método. O operador deve confirmar na maquininha antes de finalizar no sistema.",
          "Pix: funciona da mesma forma que cartão — é um registro informativo. O operador confirma o recebimento do Pix no aplicativo do banco e então seleciona Pix como método no Symplou.",
          "Fiado: o valor total da venda é lançado no saldo devedor do cliente selecionado. Para usar fiado, o cliente precisa estar previamente cadastrado na seção Clientes. Caso o cliente não tenha cadastro, o sistema solicitará o cadastro antes de concluir.",
        ],
        tips: [
          "Para estabelecimentos que aceitam pagamentos divididos (parte em dinheiro, parte no cartão), registre dois lançamentos separados ou oriente o cliente a pagar o valor exato em cada método.",
        ],
        warning:
          "O Symplou não se integra diretamente com maquininhas de cartão ou bancos. A confirmação do pagamento na maquininha ou aplicativo bancário é de responsabilidade do operador antes de registrar a venda.",
      },
      {
        title: "Copiloto de IA: sugestões inteligentes durante a venda",
        paragraphs: [
          "O copiloto de IA é um painel lateral que pode ser ativado durante qualquer venda. Ele analisa os produtos já adicionados ao carrinho, o histórico de compras do cliente selecionado (quando houver) e padrões de vendas anteriores para sugerir itens complementares ou que costumam ser comprados juntos.",
          "Para ativar o copiloto, clique no botão de IA no painel de vendas. O painel se expande e exibe as sugestões com o nome do produto, o preço e uma justificativa resumida de por que aquele item foi sugerido. Você pode adicionar qualquer sugestão ao carrinho com um único clique.",
          "Cada consulta ao copiloto consome créditos de IA da conta. O número de créditos disponíveis é exibido em Meu Plano. Se os créditos se esgotarem, o painel ficará desabilitado até que novos créditos sejam adquiridos ou o ciclo de renovação mensal ocorra, conforme o plano contratado.",
          "O copiloto aprende com o histórico de vendas do próprio negócio. Quanto mais vendas registradas, mais precisas tendem a ser as sugestões. Em negócios com menos de 30 dias de operação, as sugestões são mais genéricas.",
        ],
        tips: [
          "Use o copiloto especialmente em vendas de maior valor ou quando perceber que o cliente está indeciso — as sugestões contextuais ajudam a aumentar o ticket médio sem pressionar o cliente.",
          "O copiloto pode ser desativado por padrão se o negócio não quiser usar créditos regularmente. Ele pode ser acionado pontualmente apenas quando conveniente.",
        ],
      },
      {
        title: "Finalizando, salvando e reabrindo vendas",
        paragraphs: [
          "Ao clicar em 'Finalizar venda', o sistema exibe um resumo com o total, a forma de pagamento selecionada e os dados do cliente (se houver). Confirme para registrar a venda no histórico. A partir desse momento, a venda entra como 'concluída' e não pode ser alterada — apenas visualizada.",
          "Vendas em andamento são salvas automaticamente em tempo real. Se você fechar o navegador, desligar o computador ou navegar para outra tela sem finalizar, a venda permanece salva e pode ser retomada clicando novamente em 'Vender'. O sistema abre a última venda em aberto automaticamente.",
          "Se houver necessidade de cancelar uma venda já finalizada, entre em contato com o suporte. O Symplou não permite cancelamento direto pelo usuário para preservar a integridade do histórico financeiro. Em casos de erro operacional frequente, avalie criar uma entrada de ajuste no fluxo de caixa.",
        ],
        tips: [
          "Se um cliente desistir no meio da venda, basta sair da tela sem finalizar. A venda fica salva como 'em andamento' e pode ser descartada ou convertida em uma venda real posteriormente.",
        ],
        warning:
          "Vendas finalizadas não podem ser editadas. Verifique os itens e o método de pagamento com atenção antes de confirmar.",
      },
    ],
  },
  {
    id: "caixa",
    label: "Caixa",
    color: "#34d399",
    icon: <PointOfSaleIcon fontSize="small" />,
    description: "Abertura, movimentações, fechamento e relatórios de sessões.",
    articles: [
      {
        title: "Entendendo o fluxo de caixa no Symplou",
        paragraphs: [
          "O módulo de Caixa serve para controlar o dinheiro físico do estabelecimento — não o faturamento total, que inclui cartão e Pix. Ele funciona por sessões: uma sessão começa com a abertura do caixa (informando o troco inicial) e termina com o fechamento, quando o operador informa quanto dinheiro está fisicamente no caixa.",
          "Durante a sessão, todas as vendas pagas em dinheiro são automaticamente somadas ao saldo esperado. O operador pode também registrar entradas avulsas (ex.: dinheiro recebido de uma cobrança externa) e retiradas (ex.: pagamento de um fornecedor em dinheiro).",
          "No fechamento, o sistema compara o saldo esperado (calculado automaticamente) com o saldo informado pelo operador. A diferença é registrada como 'sobra' ou 'falta', permitindo rastrear inconsistências ao longo do tempo.",
          "O módulo de Caixa é opcional. Se o seu negócio não precisa desse controle, ele pode ser desativado em Configurações, desaparecendo completamente da interface.",
        ],
        tips: [
          "Recomenda-se abrir e fechar o caixa no início e no fim de cada turno de trabalho, mesmo que o volume de dinheiro físico seja baixo. Isso cria um histórico confiável para análise futura.",
        ],
      },
      {
        title: "Abrindo o caixa: passo a passo",
        paragraphs: [
          "Antes de iniciar o atendimento, o operador responsável deve abrir uma sessão de caixa. Isso registra o horário de início, o usuário responsável e o valor inicial de troco disponível em dinheiro.",
          "O sistema só permite uma sessão de caixa aberta por vez. Tentativas de abrir um segundo caixa enquanto há uma sessão ativa serão bloqueadas com uma mensagem de aviso.",
        ],
        steps: [
          "Acesse 'Caixa' no menu lateral.",
          "Clique no botão 'Abrir caixa'.",
          "Informe o valor de troco inicial (o dinheiro já presente no caixa antes das vendas do dia).",
          "Confirme a abertura. O sistema registra o horário e o usuário responsável.",
          "A tela do caixa exibirá o saldo atual e as opções de movimentação.",
        ],
        tips: [
          "Mesmo que o troco inicial seja zero, realize a abertura formal. Isso garante que o histórico de sessões não tenha lacunas.",
        ],
        warning:
          "Somente usuários com a permissão 'Gestão de Caixa' podem abrir, movimentar e fechar sessões. Operadores sem essa permissão conseguem realizar vendas, mas não têm acesso ao módulo de caixa.",
      },
      {
        title: "Registrando entradas e retiradas durante o turno",
        paragraphs: [
          "Além das vendas em dinheiro (que são creditadas automaticamente), o operador pode registrar movimentações avulsas durante a sessão. Entradas são valores que chegam ao caixa por outros meios — como cobrança de um fiado pago em dinheiro fora do sistema, ou repasse de troco de outro funcionário. Retiradas são saídas de dinheiro do caixa para fins operacionais.",
          "Para retiradas, o sistema exige a seleção de uma categoria. As categorias padrão incluem 'Pagar conta', que é fixa e não pode ser removida. Você pode criar categorias personalizadas em Configurações > Categorias de retirada (ex.: 'Despesa operacional', 'Retirada do sócio', 'Compra de insumos').",
          "Toda movimentação registra automaticamente o usuário responsável, o horário e o valor. Isso facilita a auditoria ao final do turno e permite identificar quem realizou cada operação em caso de inconsistências.",
        ],
        tips: [
          "Use a descrição opcional das retiradas para detalhar o destino do dinheiro. 'Pagar conta — fornecedor X, NF 1234' é muito mais útil do que apenas 'Pagar conta'.",
        ],
      },
      {
        title: "Fechando o caixa: conferência e apuração",
        paragraphs: [
          "O fechamento é o momento de conferir se o dinheiro físico no caixa bate com o que o sistema calculou. O sistema apresenta um resumo separado por forma de pagamento: dinheiro esperado (abertura + vendas em dinheiro + entradas − retiradas), cartão esperado, Pix esperado etc.",
          "O operador deve contar fisicamente o dinheiro no caixa e inserir o valor encontrado. O sistema calcula automaticamente a diferença e a registra como 'sobra' (mais dinheiro do que o esperado) ou 'falta' (menos dinheiro). Ambas as situações ficam registradas no histórico da sessão.",
          "Após confirmar o fechamento, a sessão é encerrada e não pode ser reaberta para edição. Uma nova sessão só pode ser iniciada com uma nova abertura de caixa.",
        ],
        steps: [
          "Com a sessão aberta, clique em 'Fechar caixa'.",
          "O sistema exibirá o saldo esperado por forma de pagamento.",
          "Conte fisicamente o dinheiro disponível no caixa.",
          "Insira o valor contado no campo 'Dinheiro físico'.",
          "Revise o resumo: diferença calculada, total de vendas, entradas e retiradas.",
          "Confirme o fechamento. A sessão será encerrada.",
        ],
        tips: [
          "Se houver diferença recorrente entre o esperado e o contado, verifique se todos os operadores estão registrando as retiradas corretamente ou se há vendas em dinheiro sendo finalizadas com o método de pagamento errado.",
        ],
        warning:
          "Sessões encerradas não podem ser reabertas. Se você fechar o caixa por engano, precisará abrir uma nova sessão e ajustar manualmente as movimentações.",
      },
      {
        title: "Relatórios e histórico de sessões",
        paragraphs: [
          "Em Caixa > Relatórios você encontra o histórico completo de todas as sessões, ordenadas da mais recente para a mais antiga. Para cada sessão é possível ver o horário de abertura, o horário de fechamento, o usuário responsável e o resumo financeiro.",
          "Clicando em uma sessão específica você acessa o detalhamento completo: cada movimentação (abertura, vendas, entradas, retiradas e fechamento), com horário, valor, usuário e categoria. Esse nível de detalhe é fundamental para auditorias internas e para identificar erros pontuais.",
          "O filtro de período no topo da lista de relatórios permite recortar o histórico por intervalo de datas. Use isso para analisar semanas, meses ou períodos sazonais específicos.",
        ],
        tips: [
          "Exporte ou tire prints das sessões mensalmente para manter um arquivo histórico externo ao sistema. Isso é uma boa prática para fins contábeis.",
        ],
      },
    ],
  },
  {
    id: "produtos",
    label: "Produtos e Catálogo",
    color: "#fb923c",
    icon: <InventoryIcon fontSize="small" />,
    description: "Cadastro, categorias, fornecedores, estoque e importação em massa.",
    articles: [
      {
        title: "Cadastrando um produto: campos e boas práticas",
        paragraphs: [
          "Acesse Produtos > Novo produto para abrir o formulário de cadastro. Os campos obrigatórios são nome e preço de venda. Os demais campos são opcionais, mas preenchê-los desde o início economiza retrabalho no futuro.",
          "Nome: use nomes claros e padronizados. Evite abreviações obscuras — o campo de busca da tela de venda usa o nome do produto, então quanto mais descritivo ele for, mais fácil será encontrá-lo. Ex.: 'Refrigerante Cola 350ml' em vez de 'Refri C 350'.",
          "Preço de venda: insira sempre o preço final ao cliente. O Symplou não calcula margem automaticamente — se quiser registrar o preço de custo para controle interno, use o campo de observações ou crie um padrão interno.",
          "Código de barras: essencial para usar o leitor de código de barras durante as vendas. Digite o código impresso na embalagem do produto. Não há validação de formato — o sistema aceita EAN-13, EAN-8, Code 128 etc.",
          "Categoria: vincule o produto a uma categoria para facilitar a organização do catálogo. Se a categoria ainda não existe, crie-a antes de cadastrar o produto.",
          "Fornecedor: vincule o fornecedor principal do produto para rastreabilidade. Útil para relatórios de estoque e análise de margem.",
          "Quantidade em estoque: se você controla estoque, informe a quantidade disponível no momento do cadastro. O sistema passará a debitar automaticamente a cada venda.",
        ],
        tips: [
          "Se o mesmo produto tem variações (ex.: tamanhos ou sabores diferentes), cadastre cada variação como um produto separado com nomes distintos, como 'Camiseta Azul P', 'Camiseta Azul M' etc.",
        ],
      },
      {
        title: "Organizando o catálogo com categorias",
        paragraphs: [
          "Categorias são grupos temáticos que organizam o catálogo de produtos. Elas aparecem como filtros na listagem de produtos e ajudam operadores a localizar itens durante vendas quando a busca por nome não é suficiente.",
          "Acesse Produtos > Categorias para criar, renomear ou excluir categorias. A exclusão de uma categoria só é permitida se nenhum produto estiver vinculado a ela — o sistema bloqueia a operação para evitar produtos sem categoria de forma acidental.",
          "Pense na estrutura de categorias como as prateleiras de uma loja física. Crie categorias que façam sentido para o seu operador, não apenas para você. Categorias como 'Bebidas', 'Laticínios', 'Higiene Pessoal' são mais intuitivas do que categorias baseadas no nome do fornecedor.",
        ],
        tips: [
          "Evite criar muitas categorias com poucos produtos. Um catálogo com 50 produtos não precisa de mais de 8 a 10 categorias. Muitas categorias vazias ou com 1 produto criam ruído desnecessário.",
        ],
      },
      {
        title: "Vinculando e gerenciando fornecedores",
        paragraphs: [
          "Fornecedores são as empresas ou pessoas de quem você compra os produtos para revenda ou uso. Cadastrá-los no Symplou permite vincular cada produto à sua origem, o que é útil para análises de custo, relatórios de compras e identificação de produtos de determinado fornecedor em caso de recall ou renegociação de preço.",
          "Acesse Produtos > Fornecedores para gerenciar o cadastro. Os campos incluem nome, CNPJ, telefone, e-mail e endereço. Apenas o nome é obrigatório. Mantenha os dados de contato atualizados para referência rápida quando precisar falar com o fornecedor.",
          "Um produto pode ter apenas um fornecedor principal vinculado. Se você compra o mesmo produto de fornecedores diferentes dependendo da disponibilidade, vincule o fornecedor predominante e use o campo de observações do produto para registrar os alternativos.",
        ],
        tips: [
          "Mesmo que você não pretenda usar relatórios de fornecedores agora, vale cadastrá-los corretamente desde o início. A funcionalidade se torna muito valiosa quando o catálogo cresce.",
        ],
      },
      {
        title: "Controle de estoque: funcionamento, ajustes e alertas",
        paragraphs: [
          "O estoque no Symplou funciona de forma simples: cada produto tem uma quantidade disponível. A cada venda finalizada com aquele produto, o sistema debita automaticamente a quantidade vendida. Se um produto tiver quantidade zero e for vendido, o saldo passará para negativo — isso é permitido e serve como alerta visual, mas não bloqueia a venda.",
          "Acesse Produtos > Estoque para ver a posição atual de todos os produtos cadastrados com controle de estoque ativo. A lista exibe nome, categoria, fornecedor, quantidade atual e a opção de ajuste manual.",
          "Ajustes manuais são necessários quando há entrada de mercadoria (compra de fornecedor), perda, devolução ou inventário. Para ajustar, clique no produto na tela de estoque, informe o novo saldo ou a variação (positiva ou negativa) e confirme.",
          "Produtos sem quantidade cadastrada não participam do controle de estoque — eles podem ser vendidos livremente sem qualquer debito. Para incluir um produto no controle, acesse seu formulário de edição e informe a quantidade inicial.",
        ],
        tips: [
          "Realize inventários físicos periódicos (quinzenal ou mensalmente) e ajuste o estoque no sistema para manter a acuracidade. Pequenas divergências acumuladas ao longo de meses distorcem muito os relatórios.",
          "Produtos com saldo negativo no estoque indicam que as vendas foram registradas mas a entrada de mercadoria correspondente não foi lançada. Regularize esses registros o quanto antes.",
        ],
        warning:
          "O Symplou não emite alertas automáticos de estoque mínimo ainda. Monitore manualmente os produtos críticos na tela de Estoque.",
      },
      {
        title: "Importação em massa de produtos via planilha",
        paragraphs: [
          "Quando o catálogo é grande, digitar produto por produto no formulário é inviável. O recurso de Cadastro em Massa permite importar centenas de produtos de uma vez através de uma planilha no formato especificado.",
          "Acesse Produtos > Cadastro em massa. O sistema disponibiliza um modelo de planilha para download — use exatamente esse modelo, pois o mapeamento de colunas é fixo. Preencha os dados conforme as instruções na própria planilha (nome, preço, código de barras, categoria etc.) e faça o upload do arquivo.",
          "O sistema valida cada linha antes de importar. Linhas com erros (como preço inválido ou categoria inexistente) são rejeitadas individualmente — as demais são importadas normalmente. Um relatório de erros é exibido ao final para que você corrija e reimporte as linhas problemáticas.",
          "O Cadastro em Massa também pode ser usado para atualizar preços em lote: basta exportar a planilha atual, alterar os preços e reimportar. Produtos com o mesmo nome são atualizados, não duplicados.",
        ],
        steps: [
          "Acesse Produtos > Cadastro em massa.",
          "Baixe o modelo de planilha disponibilizado na tela.",
          "Preencha os dados dos produtos conforme as colunas indicadas.",
          "Salve o arquivo no formato correto (geralmente .xlsx ou .csv).",
          "Faça o upload da planilha na tela de Cadastro em massa.",
          "Aguarde o processamento e revise o relatório de erros exibido.",
          "Corrija as linhas com erro e reimporte se necessário.",
        ],
        warning:
          "Não altere os cabeçalhos das colunas do modelo. Qualquer modificação na estrutura da planilha causará falha na importação.",
      },
      {
        title: "Gerando e imprimindo QR Codes de produtos",
        paragraphs: [
          "O Symplou gera QR Codes únicos para cada produto. Esses QR Codes podem ser impressos e fixados no produto ou na prateleira, permitindo que o leitor de câmera os identifique durante a venda.",
          "Para gerar o QR Code de um produto, acesse a lista de produtos, localize o produto desejado e clique na opção de QR Code. O sistema exibirá o código gerado e uma opção de impressão direta.",
          "O QR Code contém um identificador interno do produto, não o código de barras do fabricante. Por isso, ele só funcionará para leituras dentro do próprio Symplou — não é um EAN padrão de mercado.",
          "Use o recurso para produtos artesanais, a granel ou que não possuam código de barras impresso pelo fabricante. Assim você consegue o benefício da leitura rápida sem precisar comprar etiquetas com código EAN.",
        ],
        tips: [
          "Imprima os QR Codes em tamanho adequado (pelo menos 2x2 cm) para garantir que o scanner de câmera consiga lê-los corretamente, especialmente em ambientes com iluminação variável.",
        ],
      },
    ],
  },
  {
    id: "financeiro",
    label: "Clientes e Financeiro",
    color: "#f472b6",
    icon: <PeopleAltIcon fontSize="small" />,
    description: "Cadastro de clientes, fiado, pagamentos e relatório de crédito.",
    articles: [
      {
        title: "Cadastrando e gerenciando clientes",
        paragraphs: [
          "O módulo de Clientes centraliza o cadastro de pessoas físicas e jurídicas que compram no seu estabelecimento. Ter o cliente cadastrado é requisito para usar o sistema de fiado, mas também é útil para rastrear o histórico de compras e emitir relatórios de relacionamento.",
          "Acesse Clientes e clique em 'Novo cliente'. Os campos incluem nome completo, CPF/CNPJ, telefone, e-mail e endereço. Apenas o nome é obrigatório para o cadastro básico, mas recomenda-se preencher ao menos o CPF/CNPJ e o telefone para facilitar a identificação em caso de homônimos.",
          "Após o cadastro, o cliente aparece na listagem principal, de onde é possível visualizar o histórico de compras, o saldo de fiado atual, os pagamentos realizados e todas as interações registradas no sistema.",
        ],
        tips: [
          "Vincule o cliente à venda sempre que possível, mesmo em pagamentos à vista. Isso cria um histórico de compras valioso para análise de comportamento e para o copiloto de IA.",
        ],
      },
      {
        title: "Como funciona o sistema de fiado",
        paragraphs: [
          "Fiado é uma modalidade de crédito informal onde o cliente leva o produto agora e paga depois. No Symplou, ao finalizar uma venda com o método 'Fiado', o valor total da compra é adicionado ao saldo devedor do cliente selecionado.",
          "O saldo de fiado é acumulativo: cada nova compra no fiado é somada ao saldo anterior. O histórico mostra cada venda individualmente, com data, valor e itens comprados. Isso permite que tanto o operador quanto o cliente acompanhem detalhadamente o que está sendo cobrado.",
          "O sistema não define automaticamente um limite de crédito por cliente — qualquer valor pode ser lançado no fiado sem restrição. Se você deseja controlar o limite, faça esse controle operacionalmente (orientando os operadores a consultar o saldo antes de aceitar novas compras no fiado).",
        ],
        tips: [
          "Antes de fechar uma venda no fiado, consulte o saldo atual do cliente na tela de Clientes. Clientes com saldo elevado em aberto podem precisar de uma conversa antes de novas compras.",
        ],
        warning:
          "A venda no fiado só pode ser realizada se o cliente estiver cadastrado. O sistema bloqueia a finalização caso não haja um cliente vinculado à venda ao selecionar Fiado como forma de pagamento.",
      },
      {
        title: "Registrando pagamentos de fiado",
        paragraphs: [
          "Quando o cliente paga parte ou toda a dívida, esse pagamento precisa ser registrado manualmente no sistema para que o saldo seja atualizado corretamente. O Symplou não deduz automaticamente pagamentos de fiado.",
          "Para registrar um pagamento, acesse Clientes, localize o cliente, clique no nome para abrir o detalhe e então clique em 'Registrar pagamento'. Informe o valor pago, a data e a forma de pagamento usada (dinheiro, Pix etc.). O sistema abate o valor do saldo devedor imediatamente.",
          "Você pode registrar pagamentos parciais quantas vezes forem necessárias. Cada pagamento fica registrado no histórico do cliente com data, valor e método, criando um rastro auditável de toda a negociação.",
        ],
        steps: [
          "Acesse Clientes no menu lateral.",
          "Localize o cliente pelo nome ou CPF.",
          "Clique no nome do cliente para abrir seu perfil.",
          "Na seção de fiado, clique em 'Registrar pagamento'.",
          "Informe o valor pago, a data e a forma de pagamento.",
          "Confirme o pagamento. O saldo será atualizado automaticamente.",
        ],
        tips: [
          "Emita um comprovante informal (print da tela) quando o cliente quitar integralmente o saldo. Isso evita disputas futuras sobre o que foi ou não pago.",
        ],
      },
      {
        title: "Relatório de fiado: leitura e uso estratégico",
        paragraphs: [
          "O Relatório de Fiado, acessível em Clientes > Relatório de Fiado, exibe uma visão consolidada de todos os clientes com saldo devedor em aberto. Para cada cliente você vê o nome, o saldo total, a data da última compra e o número de transações em aberto.",
          "O relatório é essencial para a gestão de cobrança: ordene por saldo para priorizar os clientes com maior dívida, ou por data da última compra para identificar clientes inativos que ainda possuem débito.",
          "Use o relatório como base para ligar ou enviar mensagens de cobrança. O telefone do cliente está disponível no detalhe do perfil, acessível diretamente a partir do relatório.",
        ],
        tips: [
          "Revise o relatório de fiado pelo menos uma vez por semana. Saldos pequenos que se acumulam sem atenção tendem a se tornar problemas difíceis de resolver meses depois.",
        ],
      },
    ],
  },
  {
    id: "contas-a-pagar",
    label: "Contas a Pagar",
    color: "#fbbf24",
    icon: <ReceiptLongIcon fontSize="small" />,
    description: "Registro, categorização e controle de despesas e obrigações.",
    articles: [
      {
        title: "Registrando uma nova conta a pagar",
        paragraphs: [
          "O módulo de Contas a Pagar serve para registrar todas as obrigações financeiras do negócio: aluguel, fornecedores, serviços, impostos e qualquer outra despesa recorrente ou eventual. Centralizar essas informações no Symplou evita esquecimentos e facilita a visão do fluxo de caixa futuro.",
          "Acesse Contas a Pagar e clique em 'Nova conta'. Preencha a descrição (ex.: 'Aluguel — Maio/2025'), o valor, a data de vencimento e, opcionalmente, a categoria. O campo de observação pode ser usado para registrar o número da nota fiscal, o banco onde a conta será paga ou qualquer informação complementar.",
          "Após salvar, a conta aparece na lista como 'Pendente' com indicação visual de proximidade do vencimento. Contas vencidas aparecem com destaque em vermelho para fácil identificação.",
        ],
        tips: [
          "Crie o hábito de registrar as contas assim que receber as faturas ou notas fiscais, não apenas próximo ao vencimento. Isso dá uma visão antecipada das despesas futuras.",
        ],
      },
      {
        title: "Baixando contas: marcando como pagas",
        paragraphs: [
          "Quando uma conta é liquidada, você precisa registrar o pagamento no sistema para que ela saia da lista de pendências. Na lista de Contas a Pagar, localize a conta e clique no botão de confirmação de pagamento (ícone de check).",
          "O sistema solicitará a data de pagamento. Por padrão, a data atual é preenchida, mas você pode alterar para a data real do pagamento caso esteja registrando retroativamente. Após confirmar, o status da conta muda para 'Pago' e ela é movida para o histórico.",
          "Contas pagas continuam visíveis no histórico filtrado por status 'Pago'. Isso permite auditar pagamentos passados, verificar se uma conta específica já foi quitada e comparar datas de vencimento com datas de pagamento para análise de pontualidade.",
        ],
        steps: [
          "Acesse Contas a Pagar no menu.",
          "Localize a conta que foi quitada na lista.",
          "Clique no ícone de confirmação de pagamento (check).",
          "Verifique ou altere a data de pagamento.",
          "Confirme. O status muda para 'Pago'.",
        ],
        warning:
          "Contas marcadas como pagas não podem ser revertidas para 'Pendente' diretamente. Se você marcar por engano, exclua a conta e crie-a novamente com o status correto.",
      },
      {
        title: "Editando, excluindo e categorizando contas",
        paragraphs: [
          "Para editar uma conta pendente, clique no ícone de edição ao lado dela. Você pode alterar a descrição, o valor, o vencimento e a categoria. Contas já pagas não podem ser editadas — apenas excluídas e recriadas.",
          "A exclusão é permanente e não pode ser desfeita. Use-a apenas para remover registros criados por engano ou duplicatas. Para corrigir o valor de uma conta paga, a alternativa é excluí-la e criar uma nova com os dados corretos.",
          "As categorias de Contas a Pagar ajudam a analisar o perfil de despesas do negócio. Recomenda-se criar categorias como 'Aluguel e infraestrutura', 'Fornecedores', 'Folha de pagamento', 'Impostos e taxas', 'Marketing' e 'Serviços'. Essa segmentação facilita análises posteriores.",
        ],
        tips: [
          "Mantenha consistência nas categorias ao longo do tempo. Mudar critérios de categorização frequentemente prejudica a comparabilidade dos dados entre períodos.",
        ],
      },
      {
        title: "Usando filtros para gestão eficiente",
        paragraphs: [
          "A tela de Contas a Pagar oferece filtros de status (pendente/pago) e de período. Use o filtro de pendentes para focar nas contas que exigem ação imediata. Use o filtro de período para analisar o total de despesas em um determinado mês.",
          "O total de valores pendentes é exibido em destaque no topo da lista. Esse número representa o total que precisará ser desembolsado nas próximas semanas — compare com o saldo de caixa esperado para avaliar se há risco de falta de capital.",
          "Para análises mais avançadas de fluxo de caixa, combine os dados de Contas a Pagar com os relatórios de Caixa e de Vendas. O Symplou não gera um demonstrativo de fluxo de caixa consolidado automaticamente, mas os dados brutos estão disponíveis nas três seções.",
        ],
        tips: [
          "Reserve um momento semanal fixo para revisar as contas com vencimento nos próximos 7 dias. Isso evita juros por atraso e mantém a saúde financeira do negócio.",
        ],
      },
    ],
  },
  {
    id: "vendas-relatorios",
    label: "Vendas e Relatórios",
    color: "#60a5fa",
    icon: <BarChartIcon fontSize="small" />,
    description: "Estatísticas, histórico detalhado de vendas, comissões e insights com IA.",
    articles: [
      {
        title: "Painel de estatísticas: entendendo os dados",
        paragraphs: [
          "O painel de estatísticas está disponível em Vendas > Estatísticas e Relatórios. Para acessá-lo, o usuário precisa ter a permissão de Analytics e o módulo de Estatísticas deve estar ativo na assinatura.",
          "O painel exibe gráficos de faturamento total por período, número de vendas, ticket médio, ranking de produtos mais vendidos e comparativo entre períodos. Todos os dados são calculados em tempo real a partir das vendas registradas no sistema.",
          "O seletor de período no topo do painel permite escolher intervalos predefinidos (hoje, últimos 7 dias, últimos 30 dias, este mês, mês anterior) ou definir um intervalo personalizado. Os gráficos e métricas se atualizam automaticamente ao mudar o período.",
        ],
        tips: [
          "Compare o mesmo período de meses diferentes (ex.: março deste ano vs. março do ano passado) para identificar tendências sazonais no seu negócio.",
        ],
      },
      {
        title: "Analisando o ranking de produtos e ticket médio",
        paragraphs: [
          "O ranking de produtos exibe os itens mais vendidos em quantidade e em faturamento. Esses podem ser listas distintas — um produto barato pode liderar em quantidade, mas não em faturamento. Analise ambas as perspectivas para entender o que realmente sustenta a receita do negócio.",
          "O ticket médio é calculado dividindo o faturamento total pelo número de vendas no período. Um ticket médio crescente pode indicar que os clientes estão comprando mais por vez, que preços foram reajustados ou que o copiloto de IA está gerando upsell eficaz.",
          "Produtos com queda consistente no ranking ao longo de vários períodos podem indicar que o item está desatualizado, que o preço ficou fora de mercado ou que há um substituto mais popular sendo vendido. Use esses dados para tomar decisões de sortimento.",
        ],
        tips: [
          "Exporte os dados do painel periodicamente para uma planilha externa se precisar de análises mais complexas, como correlação entre clima e vendas ou análise de cesta de produtos.",
        ],
      },
      {
        title: "Histórico de vendas: buscando e visualizando registros",
        paragraphs: [
          "O histórico de vendas lista todas as transações registradas no sistema, da mais recente para a mais antiga. Você pode filtrar por período, por vendedor (se o módulo de usuários estiver configurado) e por status (concluída/cancelada).",
          "Clicando em qualquer venda você acessa os detalhes completos: cada produto com quantidade e valor unitário, o total da venda, a forma de pagamento, o cliente vinculado (se houver), o vendedor responsável e o horário exato da transação.",
          "O histórico é imutável — vendas finalizadas não podem ser alteradas no sistema. Isso garante a integridade dos dados financeiros e cria um rastro auditável de todas as operações.",
        ],
        tips: [
          "Ao investigar uma discrepância de caixa, use o histórico de vendas filtrado pelo turno em questão para verificar se todas as vendas foram registradas com o método de pagamento correto.",
        ],
      },
      {
        title: "Comissões: cálculo, visualização e configuração",
        paragraphs: [
          "O módulo de Comissões calcula automaticamente o valor de comissão de cada vendedor com base no faturamento gerado e no percentual configurado. O percentual padrão é definido em Configurações > Comissões.",
          "Acesse Vendas > Comissões para visualizar o resumo por vendedor no período selecionado. Cada linha exibe o nome do vendedor, o total de vendas, o percentual aplicado e o valor de comissão calculado.",
          "Se diferentes vendedores têm percentuais diferentes, isso deve ser controlado externamente — o Symplou aplica o mesmo percentual padrão para todos. Para cenários mais complexos de comissão, você pode exportar o histórico de vendas por vendedor e calcular manualmente.",
        ],
        tips: [
          "Mostre o relatório de comissões ao vendedor regularmente. Transparência no cálculo aumenta a motivação e reduz conflitos sobre valores devidos.",
        ],
      },
      {
        title: "Insights com IA: como gerar e interpretar",
        paragraphs: [
          "O painel de estatísticas oferece a opção de gerar insights usando inteligência artificial. Ao clicar no botão de IA no painel, o sistema envia os dados do período selecionado para análise e retorna um texto com observações sobre tendências, anomalias, oportunidades e riscos identificados.",
          "Os insights podem incluir: produtos com queda brusca de vendas, dias da semana com pico de movimento, clientes que não compram há muito tempo e têm histórico de compras frequentes, ou meses com crescimento acima do esperado.",
          "Cada geração de insight consome créditos de IA. O custo varia conforme o volume de dados analisados. Créditos disponíveis são exibidos em Meu Plano.",
          "Interprete os insights como sugestões, não como diagnósticos definitivos. A IA analisa padrões nos dados históricos, mas não tem conhecimento do contexto externo do seu negócio (mudanças no bairro, sazonalidade local, concorrência etc.). Use os insights como ponto de partida para reflexão.",
        ],
        tips: [
          "Gere insights ao final de cada mês como parte de uma rotina de análise. Guarde os textos gerados para comparar as recomendações ao longo do tempo e verificar quais tendências se confirmaram.",
        ],
      },
    ],
  },
  {
    id: "administracao",
    label: "Administração",
    color: "#a78bfa",
    icon: <AdminPanelSettingsIcon fontSize="small" />,
    description: "Usuários, permissões, plano, módulos, créditos de IA e configurações.",
    articles: [
      {
        title: "Convidando e gerenciando usuários",
        paragraphs: [
          "O Symplou suporta múltiplos usuários por conta. Cada colaborador deve ter seu próprio login — compartilhar credenciais compromete a rastreabilidade de vendas, retiradas de caixa e outras operações.",
          "Para convidar um colaborador, acesse Usuários e clique em 'Convidar usuário'. Informe o e-mail do colaborador e selecione as permissões adequadas para o cargo. O sistema enviará um e-mail com as instruções de acesso. O colaborador deve criar sua senha ao acessar o link de convite.",
          "Após o primeiro acesso, o colaborador aparece na lista de usuários com seu status ativo. O administrador pode editar as permissões a qualquer momento ou desativar o acesso de um usuário que não trabalha mais no estabelecimento.",
        ],
        steps: [
          "Acesse Usuários no menu lateral.",
          "Clique em 'Convidar usuário'.",
          "Informe o e-mail do colaborador.",
          "Selecione as permissões adequadas ao cargo.",
          "Envie o convite. O colaborador receberá um e-mail com instruções.",
          "Acompanhe o status do convite na lista de usuários.",
        ],
        warning:
          "Remova o acesso de colaboradores desligados imediatamente. Um usuário inativo que mantém acesso pode visualizar ou manipular dados do negócio.",
      },
      {
        title: "Mapa de permissões: o que cada uma libera",
        paragraphs: [
          "O Symplou usa um sistema de permissões granular. Cada permissão libera um conjunto específico de funcionalidades. As permissões disponíveis são:",
          "Vendas: permite acessar o ponto de venda (tela Vender) e realizar transações. Sem essa permissão, o colaborador não consegue iniciar ou finalizar vendas.",
          "Escrita de produtos: permite criar, editar e excluir produtos, categorias, fornecedores e ajustar estoque. Sem ela, o colaborador pode apenas visualizar o catálogo.",
          "Gestão de caixa: permite abrir, movimentar e fechar sessões de caixa. Vendas continuam funcionando normalmente sem essa permissão.",
          "Contas a pagar: permite criar, editar e baixar contas no módulo financeiro.",
          "Gestão de clientes: permite cadastrar e editar clientes, registrar pagamentos de fiado e acessar relatórios de crédito.",
          "Analytics: permite acessar o painel de estatísticas, histórico de vendas e comissões.",
          "Administração: permite acessar todas as seções acima, gerenciar usuários, configurações, plano e módulos. É a permissão mais ampla e deve ser restrita a gestores e proprietários.",
        ],
        tips: [
          "Para operadores de caixa típicos, as permissões de Vendas e Gestão de caixa são suficientes. Administre o princípio do menor privilégio: conceda apenas o necessário para cada função.",
        ],
      },
      {
        title: "Meu Plano: assinatura, cobrança e histórico",
        paragraphs: [
          "Em Administração > Meu Plano você encontra todos os detalhes da sua assinatura: plano atual, valor, data de renovação, método de pagamento cadastrado e histórico de faturas.",
          "O método de pagamento pode ser atualizado diretamente na tela de Meu Plano. Clique em 'Atualizar cartão' para inserir um novo cartão de crédito. O novo cartão será usado nas próximas cobranças.",
          "O histórico de faturas exibe cada cobrança realizada, com data, valor e status (pago, falhou ou pendente). Em caso de cobrança com falha, o sistema tenta novamente automaticamente nos dias seguintes. Se a falha persistir, o acesso pode ser suspenso — atualize o método de pagamento assim que possível.",
        ],
        tips: [
          "Mantenha o e-mail de contato da conta sempre atualizado. Notificações de cobrança, renovação e suspensão são enviadas para esse endereço.",
        ],
      },
      {
        title: "Ativando e desativando módulos",
        paragraphs: [
          "O Symplou é modular: funcionalidades como Estatísticas e Relatórios, Controle de Caixa e outras podem ser ativadas ou desativadas conforme a necessidade do negócio. Módulos inativos não aparecem no menu lateral, mantendo a interface limpa.",
          "Acesse Meu Plano > Módulos para ver os módulos disponíveis no seu plano. Cada módulo exibe uma descrição, o status atual e o custo adicional (se houver). Clique em 'Ativar' ou 'Desativar' conforme desejado.",
          "A ativação de um módulo é imediata — ele aparece no menu em poucos segundos. A desativação também é imediata, mas dados existentes são preservados: ao reativar, o histórico está intacto.",
        ],
        warning:
          "Desativar um módulo não exclui dados. Vendas, sessões de caixa e relatórios já registrados continuam salvos e ficam acessíveis ao reativar o módulo.",
      },
      {
        title: "Créditos de IA: o que são, como usar e como adquirir",
        paragraphs: [
          "Créditos de IA são a moeda para usar as funcionalidades de inteligência artificial do Symplou: copiloto de vendas, insights de estatísticas e quaisquer outras análises geradas por IA. Cada operação consome uma quantidade de créditos proporcional ao volume de dados processados.",
          "O saldo de créditos disponível é exibido em Meu Plano > Créditos de IA. Você pode acompanhar o consumo histórico e verificar quando o ciclo de renovação ocorrerá.",
          "Dependendo do plano, um volume de créditos é incluído mensalmente. Créditos extras podem ser adquiridos quando o saldo mensal se esgota antes do fim do ciclo. Créditos não utilizados em um mês não são acumulados para o mês seguinte — consulte os termos do seu plano para confirmar.",
        ],
        tips: [
          "Monitore o consumo de créditos semanalmente se o seu negócio usa as funcionalidades de IA com frequência. Ficar sem créditos no meio do mês impede o uso do copiloto nas vendas.",
        ],
      },
      {
        title: "Configurações gerais: boas práticas",
        paragraphs: [
          "A tela de Configurações reúne as preferências operacionais do negócio. As principais configurações disponíveis são: nome do negócio, percentual padrão de comissão e categorias de retirada do caixa.",
          "O nome do negócio é exibido no menu lateral e em relatórios. Mantenha-o atualizado se o nome comercial do estabelecimento mudar.",
          "O percentual de comissão padrão define a base de cálculo para o relatório de Comissões. Altere-o com cautela — a mudança afeta o cálculo de todos os períodos futuros imediatamente.",
          "As categorias de retirada são os tipos de saída de caixa que os operadores podem selecionar ao registrar uma retirada. Crie categorias descritivas e relevantes para o seu negócio. A categoria 'Pagar conta' é fixa e não pode ser removida.",
          "Qualquer alteração nas configurações só é aplicada ao clicar em 'Salvar alterações'. Navegar para outra tela sem salvar descartará todas as modificações feitas na sessão atual.",
        ],
        warning:
          "Somente usuários com permissão de Administração têm acesso à tela de Configurações. Operadores e vendedores comuns não visualizam essa seção.",
      },
    ],
  },
];

const ArticleBody = ({article, color}: {article: Article; color: string}) => (
  <Stack spacing={2}>
    {article.paragraphs.map((p, i) => (
      <Typography key={i} variant="body2" color="text.secondary" sx={{lineHeight: 1.8}}>
        {p}
      </Typography>
    ))}

    {article.steps && article.steps.length > 0 && (
      <Box
        sx={{
          bgcolor: (t) => alpha(color, t.palette.mode === "dark" ? 0.06 : 0.05),
          border: `1px solid ${alpha(color, 0.2)}`,
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            display: "block",
            mb: 1.25,
          }}
        >
          Passo a passo
        </Typography>
        <Stack spacing={1}>
          {article.steps.map((step, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  minWidth: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: color,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 800,
                  mt: "1px",
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.7}}>
                {step}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    )}

    {article.warning && (
      <Alert severity="warning" sx={{borderRadius: 2, "& .MuiAlert-message": {fontSize: 13}}}>
        {article.warning}
      </Alert>
    )}

    {article.tips && article.tips.length > 0 && (
      <Stack spacing={1}>
        {article.tips.map((tip, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{
              bgcolor: (t) => alpha(t.palette.info.main, 0.06),
              border: (t) => `1px solid ${alpha(t.palette.info.main, 0.18)}`,
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <LightbulbOutlinedIcon
              sx={{fontSize: 17, color: "info.main", mt: "2px", flexShrink: 0}}
            />
            <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.7}}>
              {tip}
            </Typography>
          </Stack>
        ))}
      </Stack>
    )}
  </Stack>
);

export const KnowledgeBasePage = () => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | false>(false);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredModules = modules
    .map((mod) => ({
      ...mod,
      articles: mod.articles.filter(
        (a) =>
          !normalizedSearch ||
          a.title.toLowerCase().includes(normalizedSearch) ||
          a.paragraphs.some((p) => p.toLowerCase().includes(normalizedSearch)) ||
          (a.steps ?? []).some((s) => s.toLowerCase().includes(normalizedSearch)) ||
          (a.tips ?? []).some((t) => t.toLowerCase().includes(normalizedSearch)) ||
          mod.label.toLowerCase().includes(normalizedSearch)
      ),
    }))
    .filter((mod) => mod.articles.length > 0);

  const handleAccordion =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const totalArticles = modules.reduce((acc, m) => acc + m.articles.length, 0);

  return (
    <Shell title="Base de Conhecimento">
      <PageHeader
        title="Base de Conhecimento"
        description="Guias completos para todos os recursos do Symplou, organizados por módulo."
      />

      <Stack
        direction={{xs: "column", sm: "row"}}
        spacing={1.5}
        alignItems={{sm: "center"}}
        sx={{mb: 3}}
      >
        <TextField
          fullWidth
          placeholder="Buscar por tópico, módulo ou palavra-chave…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setExpanded(false);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{whiteSpace: "nowrap", flexShrink: 0}}
        >
          {modules.length} módulos · {totalArticles} artigos
        </Typography>
      </Stack>

      {filteredModules.length === 0 ? (
        <Box sx={{py: 8, textAlign: "center", color: "text.secondary"}}>
          <SearchIcon sx={{fontSize: 48, opacity: 0.3, mb: 1}} />
          <Typography variant="body1">
            Nenhum resultado encontrado para "{search}"
          </Typography>
        </Box>
      ) : (
        <Stack spacing={5}>
          {filteredModules.map((mod) => (
            <Box key={mod.id}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 2}}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha(mod.color, 0.14),
                    color: mod.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {mod.icon}
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {mod.label}
                    </Typography>
                    <Chip
                      label={`${mod.articles.length} ${
                        mod.articles.length === 1 ? "artigo" : "artigos"
                      }`}
                      size="small"
                      sx={{
                        bgcolor: alpha(mod.color, 0.12),
                        color: mod.color,
                        fontWeight: 600,
                        fontSize: 11,
                        height: 20,
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {mod.description}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{borderLeft: `3px solid ${alpha(mod.color, 0.35)}`, pl: 2}}>
                {mod.articles.map((article, idx) => (
                  <Accordion
                    key={idx}
                    expanded={expanded === `${mod.id}-${idx}`}
                    onChange={handleAccordion(`${mod.id}-${idx}`)}
                    elevation={0}
                    disableGutters
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: "8px !important",
                      mb: 1,
                      "&:before": {display: "none"},
                      "&.Mui-expanded": {
                        borderColor: alpha(mod.color, 0.4),
                        bgcolor: alpha(mod.color, 0.02),
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{
                            color:
                              expanded === `${mod.id}-${idx}`
                                ? mod.color
                                : "text.secondary",
                            fontSize: 20,
                          }}
                        />
                      }
                      sx={{
                        minHeight: 52,
                        "& .MuiAccordionSummary-content": {my: 1.5},
                        px: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color:
                            expanded === `${mod.id}-${idx}`
                              ? mod.color
                              : "text.primary",
                        }}
                      >
                        {article.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{px: 2.5, pt: 0.5, pb: 2.5}}>
                      <ArticleBody article={article} color={mod.color} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Shell>
  );
};
